import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import AuthModal from "../AuthModal";

type CardState = "hidden" | "docked" | "fullscreen";

interface Message {
  id: string;
  content: string;
  sender: "user" | "system";
  timestamp: Date;
  type?: "default" | "criteria-response";
  showImage?: boolean;
  showBlogButton?: boolean;
  secondaryContent?: string;
}

interface ChatCardProps {
  cardState: CardState;
  isMobile: boolean;
  onDockClick?: () => void;
}

const ChatCard: React.FC<ChatCardProps> = ({ cardState, isMobile, onDockClick }) => {
  const [isResisting, setIsResisting] = useState(false);
  const [hasMounted, setHasMounted] = useState(false); // ✅ Added back

  useEffect(() => {
    // Delay chat card to appear after header/footer complete their slide-in
    const timer = setTimeout(() => setHasMounted(true), 2300);
    return () => clearTimeout(timer);
  }, []);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: `🚧  🚧

The Red Chat Card isn't chatting just yet, but our White Blog Card is live — and it's the best way to follow our rollout. Be sure to start with our first post, which shares the vision behind Carline Consensor.

You can open the Blog Card by pulling up from the bottom of this page, or by tapping the button below:`,
      sender: "system",
      timestamp: new Date(),
      showImage: true,
      showBlogButton: true,
      secondaryContent: `This is a brand-new website, so a few bugs are expected. If you spot anything strange or have suggestions, we'd love to hear from you at contact@carlineconsensor.com

Thank you for being part of the early journey — your feedback truly helps shape what comes next!`,
    },
  ]);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Expose send message function for external use
  useEffect(() => {
    (window as any).sendChatMessage = (message: string) => {
      handleSendMessage(message);
    };
  }, []);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: message,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    if (message.toLowerCase().includes("sign in")) {
      setShowAuthModal(true);
      return;
    }

    setIsTyping(true);

    setTimeout(() => {
      let botResponse = "";

      if (message.toLowerCase().includes("help")) {
        botResponse = `I can help you find the perfect extracurricular activities for your family! Here's how we can start:

• Tell me about your children (ages, interests, current activities)
• Share your schedule constraints (work hours, other commitments)
• Let me know your location or preferred area
• Mention any specific activities you're considering
• Ask about activities for specific age groups or interests

What would you like to explore first?`;
      } else {
        botResponse = `Thanks for sharing that! I'm processing your information and will help you find the best activities for your family. 

Could you tell me a bit more about:
- Your children's ages and current interests?  
- Any scheduling preferences or constraints?
- Your general location or preferred area?

This will help me provide more personalized recommendations.`;
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: botResponse,
        sender: "system",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  const handleClick = () => {
    setIsResisting(true);
    setTimeout(() => setIsResisting(false), 500);
    onDockClick?.();
  };

  return (
    <div
      className={`
        fixed ${isMobile ? "left-0 top-8 bottom-6" : "left-0 top-8 bottom-6"}
        z-30 cursor-pointer transform-gpu will-change-transform
        ${hasMounted ? "animate-slide-in-left" : "-translate-x-full"}
      `}
    >
      <div
        onClick={handleClick}
        className={`
          h-full transform-gpu will-change-transform
          ${isResisting ? "animate-bounce-resist" : ""}
        `}
      >
        <div
          className={`
            bg-chat-red w-[22px] -ml-[4px] rounded-r-lg shadow-lg flex flex-col items-end
            pr-2 hover:bg-chat-red/90 transition-colors h-full justify-center relative
          `}
        >
          <div className="flex flex-col gap-1">
            <div className="w-0.5 h-4 bg-white rounded"></div>
            <div className="w-0.5 h-4 bg-white rounded"></div>
            <div className="w-0.5 h-4 bg-white rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatCard;
